import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  AgentModelDto,
  getAgentModels,
  getAgentPreferences,
  setAgentPreferences,
} from '../api/agentModelsClient';
import { subscribeUnknownModelRecovery } from './agentModelsRecovery';

const SESSION_STORAGE_KEY = 'helios:agent:sessionModelId';

function readSessionSelection(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeSessionSelection(value: string | null): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (value === null) {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, value);
    }
  } catch {
    // ignore quota / privacy-mode errors
  }
}

/**
 * Options for the two catalogue reads.
 *
 * BUG-139.3-003 — both reads are once-per-app-session by design (a chat tab may live
 * for hours and the catalogue almost never moves). `force` is the escape hatch for the
 * one case where the server has just told us the cached copy is wrong: an explicit
 * model id rejected with `400 Unknown model`. It skips the once-per-session gate but
 * NOT the single-flight guard, so a forced read joins a read already in flight rather
 * than racing it.
 */
export interface AgentModelsFetchOptions {
  force?: boolean;
}

interface AgentModelsState {
  models: AgentModelDto[];
  systemDefaultModelId: string | null;
  userPreferredModelId: string | null;
  selectedModelIdForSession: string | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  /** Why the last `setUserPreference` failed, or null if it succeeded / never ran.
   *  Separate from `error` on purpose (BUG-139.3-003): `error` is written by the two
   *  background reads as well, so a picker's failed refresh elsewhere in the app would
   *  otherwise render as "your save failed" in Profile. This field is only ever the
   *  outcome of an explicit save the user just asked for. */
  saveError: string | null;
  /** True once the stored user preference has been fetched successfully.
   *  Further `fetchUserPreference()` calls become no-ops for the rest of the
   *  app session (same lifetime as the `models` catalogue) unless they pass
   *  `{ force: true }`; in-session changes flow through `setUserPreference`,
   *  which updates the store directly. */
  preferenceLoaded: boolean;

  // Effective id the chat composer should send with the next request.
  // Mirrors the backend resolver: per-request > user preference > system default.
  effectiveModelId: () => string | null;

  /**
   * Whether the effective model can view images.
   *
   * Tri-state on purpose. `null` means *unknown* — the catalogue has not loaded,
   * no model resolves, or the resolved id is not in the catalogue — and is NOT
   * the same as `false`. Conflating them would let the UI tell a user their model
   * cannot see images purely because the catalogue had not arrived yet, which is
   * a warning about nothing. Callers should say nothing on `null`.
   */
  effectiveModelSupportsVision: () => boolean | null;

  fetchModels: (options?: AgentModelsFetchOptions) => Promise<void>;
  fetchUserPreference: (options?: AgentModelsFetchOptions) => Promise<void>;
  setUserPreference: (modelId: string | null) => Promise<void>;
  /** Discard the last save failure. `saveError` describes one save the user just
   *  asked for, but this store is module-scope and outlives the surface that shows
   *  it, so the surface must hand it back on mount and unmount — otherwise a failed
   *  save is re-rendered (and re-announced, it is a `role="alert"`) on every later
   *  visit to a page the user merely navigated to (BUG-139.3-005). */
  clearSaveError: () => void;
  setSessionSelection: (modelId: string | null) => void;
}

// Single-flight guards: concurrent callers share the same in-flight request
// instead of firing duplicates. Both GETs are idempotent, and the picker /
// profile effects can legitimately re-run several times during one load
// (dependency churn, StrictMode double-mount, panel remounts) — the guards
// make those re-runs free without changing any caller.
let inFlightFetchModels: Promise<void> | null = null;
let inFlightFetchPreference: Promise<void> | null = null;

export const useAgentModelsStore = create<AgentModelsState>()(
  devtools(
    (set, get) => ({
      models: [],
      systemDefaultModelId: null,
      userPreferredModelId: null,
      selectedModelIdForSession: readSessionSelection(),
      isLoading: false,
      isSaving: false,
      error: null,
      saveError: null,
      preferenceLoaded: false,

      effectiveModelId: () => {
        const s = get();
        // BUG-139.3-002 — once the catalogue is loaded, a pin it doesn't contain is never
        // sendable (the backend hard-400s an explicit unknown id). Before it loads, the pin
        // passes through: an empty catalogue can't distinguish "retired" from "not yet
        // fetched", and dropping a valid pin would silently send the default model instead.
        const sessionPin =
          s.selectedModelIdForSession !== null &&
          s.models.length > 0 &&
          !s.models.some((m) => m.id === s.selectedModelIdForSession)
            ? null
            : s.selectedModelIdForSession;
        return sessionPin ?? s.userPreferredModelId ?? s.systemDefaultModelId ?? null;
      },

      effectiveModelSupportsVision: () => {
        const s = get();
        const modelId = s.effectiveModelId();
        if (modelId === null) return null;
        const model = s.models.find((m) => m.id === modelId);
        // Not in the catalogue: either it has not loaded, or the stored session /
        // preference id refers to a model that no longer exists. Both are
        // "unknown", never "cannot see".
        return model === undefined ? null : model.supportsVision;
      },

      fetchModels: async (options) => {
        // Loaded once per app session; a failed attempt leaves `models` empty
        // so the next call retries (after the backoff window). `force` re-reads
        // anyway — see AgentModelsFetchOptions (BUG-139.3-003).
        if (!options?.force && get().models.length > 0) return;
        if (inFlightFetchModels) return inFlightFetchModels;
        set({ isLoading: true, error: null }, false, 'agentModels/fetchModels:start');
        inFlightFetchModels = (async () => {
          try {
            const models = await getAgentModels();
            const systemDefault = models.find((m) => m.isDefault)?.id ?? models[0]?.id ?? null;
            // BUG-139.3-002 — drop a restored pin that names a model no longer in the
            // catalogue; keeping it would 400 every send against a bare picker placeholder.
            const currentSessionPin = get().selectedModelIdForSession;
            const pinIsValid =
              currentSessionPin === null || models.some((m) => m.id === currentSessionPin);
            if (!pinIsValid) {
              writeSessionSelection(null);
            }
            set(
              {
                models,
                systemDefaultModelId: systemDefault,
                isLoading: false,
                selectedModelIdForSession: pinIsValid ? currentSessionPin : null,
              },
              false,
              'agentModels/fetchModels:success'
            );
          } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to load models';
            set({ error: message, isLoading: false }, false, 'agentModels/fetchModels:error');
          } finally {
            inFlightFetchModels = null;
          }
        })();
        return inFlightFetchModels;
      },

      fetchUserPreference: async (options) => {
        // Loaded once per app session; a failed attempt leaves the flag unset
        // so the next call retries. `force` re-reads anyway — see
        // AgentModelsFetchOptions (BUG-139.3-003).
        if (!options?.force && get().preferenceLoaded) return;
        if (inFlightFetchPreference) return inFlightFetchPreference;
        inFlightFetchPreference = (async () => {
          try {
            const { preferredModelId } = await getAgentPreferences();
            set(
              { userPreferredModelId: preferredModelId, preferenceLoaded: true },
              false,
              'agentModels/fetchUserPreference:success'
            );
          } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to load preference';
            set({ error: message }, false, 'agentModels/fetchUserPreference:error');
          } finally {
            inFlightFetchPreference = null;
          }
        })();
        return inFlightFetchPreference;
      },

      setUserPreference: async (modelId: string | null) => {
        set(
          { isSaving: true, error: null, saveError: null },
          false,
          'agentModels/setUserPreference:start'
        );
        try {
          const { preferredModelId } = await setAgentPreferences(modelId);
          set(
            { userPreferredModelId: preferredModelId, isSaving: false, saveError: null },
            false,
            'agentModels/setUserPreference:success'
          );
        } catch (e) {
          // The message may already have been rewritten into something actionable by the
          // unknown-model recovery in the transport — surface it rather than a generic string.
          const message = e instanceof Error ? e.message : 'Failed to save preference';
          set(
            { error: message, saveError: message, isSaving: false },
            false,
            'agentModels/setUserPreference:error'
          );
        }
      },

      clearSaveError: () => {
        // Cheap to call unconditionally, but skip a no-op `set` so an idle Profile
        // visit does not push a state change (and a devtools entry) per mount.
        if (get().saveError === null) return;
        set({ saveError: null }, false, 'agentModels/clearSaveError');
      },

      setSessionSelection: (modelId: string | null) => {
        writeSessionSelection(modelId);
        set({ selectedModelIdForSession: modelId }, false, 'agentModels/setSessionSelection');
      },
    }),
    { name: 'agentModelsStore' }
  )
);

// BUG-139.3-003 — this store owns both cached reads, so it owns their invalidation. When
// the server rejects a model id it no longer knows, re-take both past their
// once-per-app-session gates: the catalogue read then reconciles a stale session pin and
// the preference read picks up the `null` the server already returns for an id it cannot
// resolve, so nothing has to be cleared by hand.
//
// Wired here, at module scope, because the recovery must be listening before any picker
// mounts or any request is issued. The subscription is idempotent, and it only touches
// `window` — nothing on the transport — so it is inert in the many suites that stand in a
// partial `axiosInstance` double.
subscribeUnknownModelRecovery(async () => {
  const { fetchModels, fetchUserPreference } = useAgentModelsStore.getState();
  await Promise.all([fetchModels({ force: true }), fetchUserPreference({ force: true })]);
});
